/**
 * Image Generation API Funktionen
 */

import { apiClient, getCsrfToken } from '../api-client';
import type { GenerateImageRequest, GenerateImageResponse } from '@/types';
import i18n from '@/i18n';

/**
 * Bild generieren
 * 
 * Sendet Bild + Parameter an die Generation-API.
 * Verwendet multipart/form-data für den File-Upload.
 */
export async function generateImage(data: GenerateImageRequest): Promise<GenerateImageResponse> {
  const formData = new FormData();
  
  // CSRF-Token als FormData-Feld (Flask-WTF erwartet dies bei Formularen)
  const csrfToken = await getCsrfToken();
  if (csrfToken) {
    formData.append('csrf_token', csrfToken);
  }
  
  // Pflichtfelder
  formData.append('image', data.image);
  formData.append('ratio', data.ratio);
  formData.append('preset_type', data.preset_type);
  formData.append('delivery_method', data.delivery_method);
  
  // Optionale Felder
  if (data.prompt_input) {
    formData.append('prompt_input', data.prompt_input);
  }
  if (data.image_format) {
    formData.append('image_format', data.image_format);
  }
  if (data.filename_prefix) {
    formData.append('filename_prefix', data.filename_prefix);
  }
  if (data.filename_suffix) {
    formData.append('filename_suffix', data.filename_suffix);
  }
  if (data.png_background) {
    formData.append('png_background', data.png_background);
  }
  if (data.png_quality !== undefined) {
    formData.append('png_quality', data.png_quality.toString());
  }
  if (data.custom_instruction_id) {
    formData.append('custom_instruction_id', data.custom_instruction_id);
  }
  if (data.source_image_id) {
    formData.append('source_image_id', data.source_image_id);
  }
  // Auto-Tags für DAM System (als JSON-Array)
  if (data.auto_tags && data.auto_tags.length > 0) {
    formData.append('auto_tags', JSON.stringify(data.auto_tags));
  }

  const response = await apiClient.post('/api/workspace/generate', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return response.data;
}

/**
 * Prüft ob genug Credits für eine Generierung vorhanden sind
 */
export async function checkCredits(): Promise<{
  success: boolean;
  credits: number;
  cost: number;
  sufficient: boolean;
}> {
  const response = await apiClient.get('/api/workspace/config');
  const config = response.data.config;

  return {
    success: true,
    credits: config.user_credits,
    cost: config.cost,
    sufficient: config.user_credits >= config.cost,
  };
}

/**
 * Bild generieren V2 - Direkt via Gemini 3 Pro Image Preview
 *
 * Text + Image → Image via Gemini API (nicht agentic)
 */
export interface GenerateImageV2Request {
  image: File;
  prompt_input: string;
  ratio: string;
  resolution: string; // '1k', '2k', '4k'
  custom_instruction_id?: string; // Nur Custom Presets in V2
  delivery_method: 'wait' | 'mail';
  image_count?: string; // '1', '2', '3', '4'
  image_format?: string; // 'png', 'jpeg', 'webp'
  filename_prefix?: string;
  filename_suffix?: string;
  png_quality?: string; // 'very_high', 'web', 'compact', 'minimal'
  brand_id?: string; // Brand für Generierung (Issue #203)
  referenced_asset_ids?: string[]; // Brand Asset UUIDs from @-mentions
  model?: string; // Model code (Nano Banana 2 integration)
  gen_quality?: string; // 'medium' | 'high' — gpt-image-2 generation quality slider
  reference_image_ids?: string[]; // Reference image asset IDs for multi-image generation
  user_language?: string; // User's current UI language for template i18n
}

export async function generateImageV2(data: GenerateImageV2Request): Promise<GenerateImageResponse> {
  const formData = new FormData();

  // CSRF-Token
  const csrfToken = await getCsrfToken();
  if (csrfToken) {
    formData.append('csrf_token', csrfToken);
  }

  // Pflichtfelder
  formData.append('image', data.image);
  formData.append('prompt_input', data.prompt_input);
  formData.append('ratio', data.ratio);
  formData.append('resolution', data.resolution);
  formData.append('delivery_method', data.delivery_method);

  // Optionale Felder
  if (data.custom_instruction_id) {
    formData.append('custom_instruction_id', data.custom_instruction_id);
  }
  if (data.image_count) {
    formData.append('image_count', data.image_count);
  }
  if (data.image_format) {
    formData.append('image_format', data.image_format);
  }
  if (data.filename_prefix) {
    formData.append('filename_prefix', data.filename_prefix);
  }
  if (data.filename_suffix) {
    formData.append('filename_suffix', data.filename_suffix);
  }
  if (data.png_quality) {
    formData.append('png_quality', data.png_quality);
  }
  if (data.brand_id) {
    formData.append('brand_id', data.brand_id);
  }
  if (data.referenced_asset_ids && data.referenced_asset_ids.length > 0) {
    formData.append('referenced_asset_ids', JSON.stringify(data.referenced_asset_ids));
  }
  if (data.model) {
    formData.append('model', data.model);
  }
  if (data.gen_quality) {
    formData.append('gen_quality', data.gen_quality);
  }
  if (data.reference_image_ids && data.reference_image_ids.length > 0) {
    formData.append('reference_image_ids', JSON.stringify(data.reference_image_ids));
  }

  // User language for template i18n (text rendering in user's language)
  formData.append('user_language', data.user_language ?? i18n.language);

  const response = await apiClient.post('/api/v2/generate/gemini-direct', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
}

